import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { type KnowledgeItem } from "./model";

export interface KnowledgeResponse {
  items?: KnowledgeItem[];
  item?: KnowledgeItem;
  needsProcessing?: boolean;
  processingError?: string;
  cleanupWarning?: boolean;
  path?: string;
  token?: string;
  url?: string;
  answer?: string;
  sources?: { id: string; name: string; text: string }[];
}

export async function knowledgeRequest(businessId: string, action: string, payload: Record<string, unknown> = {}): Promise<KnowledgeResponse> {
  const { data, error } = await supabase.functions.invoke("knowledge-library", { body: { ...payload, action, business_id: businessId } });
  if (error) {
    const response = (error as { context?: Response }).context;
    const details = response instanceof Response ? await response.clone().json().catch(() => null) : null;
    throw new Error(details?.error ?? error.message ?? "Could not complete this request.");
  }
  if (data?.error) throw new Error(data.error);
  return data;
}

export function useKnowledgeLibrarySources(businessId: string | null, processing = false) {
  return useQuery({
    queryKey: ["knowledge-library", businessId],
    enabled: !!businessId,
    queryFn: async (): Promise<KnowledgeItem[]> => (await knowledgeRequest(businessId!, "list")).items ?? [],
    refetchInterval: query => processing || query.state.data?.some(item => item.status === "Processing") ? 2000 : false,
  });
}

// Display contact notes and uploads in the drive without copying them into business-wide knowledge.
export function useContactKnowledgeSources(businessId: string | null) {
  return useQuery({
    queryKey: ["contact-knowledge-sources", businessId],
    enabled: !!businessId,
    queryFn: async (): Promise<KnowledgeItem[]> => (await knowledgeRequest(businessId!, "list_contacts")).items ?? [],
    refetchInterval: query => query.state.data?.some(item => item.status === "Processing") ? 2000 : false,
  });
}

export async function knowledgeFileUrl(businessId: string, item: KnowledgeItem): Promise<string> {
  const result = await knowledgeRequest(businessId, "file_url", { id: item.id });
  if (!result.url) throw new Error("The original file is unavailable.");
  return result.url;
}
